
import { Inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, delay, map } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { IShippingModel } from "../models/shipping/shipping.model";
import { ShippingMapping } from "../mappings/shipping.mapping";
import { ShippingPutRequestDtoModel } from "../models/shipping/dto/shipping-put-dto.model";
import { IShippingDtoModel } from "../models/shipping/dto/shipping.dto.model";
import { EnvironmentService } from "./environment.service";
import { ApplicationContextService } from "./application-context.service";
import { IShippingProvider } from "../models/shipping/shipping-provider.model";
import { IShippingMethod } from "../models/shipping/shipping-method.model";
import { CoreService } from "./customer-core.service";


@Injectable({
    providedIn: 'root'
  })
export class ShippingService {
    private mapping: ShippingMapping;

    apiGatewayRequestOptions = {
               headers: new HttpHeaders({ "apimRequest": 'true', "Ocp-Apim-Subscription-Key": (this.envService.settings.apimSubscriptionKey ? this.envService.settings.apimSubscriptionKey : '') })
            };

   constructor(private readonly httpClient: HttpClient, 
               public envService: EnvironmentService,
               private applicationContext: ApplicationContextService,
               private customerService: CoreService) {
      this.mapping = new ShippingMapping();   
   }

   private path() {
      if (this.applicationContext.isPublic()) {
         return `${this.envService.settings.publicApiUrl}/Customers/shipping`;
      }

      return `${this.envService.settings.apiUrl}/Customers`;
   }

   public get(customerId: string): Observable<IShippingModel> {
      if (this.applicationContext.isPublic()) {
         return this.httpClient.get<IShippingDtoModel>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
            map(resultDto => this.mapping.toApplicationModel(resultDto))
         );
      }

      return this.httpClient.get<IShippingDtoModel>(`${this.path()}/${customerId}/shipping`).pipe(
         map(resultDto => this.mapping.toApplicationModel(resultDto))
      );
   }

   public update(customerId: string, model: IShippingModel): Observable<ShippingPutRequestDtoModel> {
      const dto = this.mapping.toDtoPutModel(model, this.customerService.getCustomerId(customerId) as string);

      if (this.applicationContext.isPublic()) {
         return this.httpClient.put<ShippingPutRequestDtoModel>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`, dto);
      }

      return this.httpClient.put<ShippingPutRequestDtoModel>(`${this.path()}/${customerId}/shipping`, dto);
   }

   public getShippingProviders(): Observable<IShippingProvider[]>{

      return this.httpClient.get<any>(`${this.envService.settings.shippingManagementApi}/providers?branchCode=${this.applicationContext.getBranchCodeFromUrl()}`, this.apiGatewayRequestOptions)
      .pipe(
         map(res => 

            res.branchProviders.map((bp:any) => ({
               code: bp.provider.code,
               name: bp.provider.name,
               legacyId: bp.provider.legacyId
            })).sort((a:any, b:any) => a.name.localeCompare(b.name))

         )
      );
  }

  public getShippingMethods(shippingProvider?: IShippingProvider): Observable<IShippingMethod[]>{

   let url = `${this.envService.settings.shippingManagementApi}/methods?branchCode=${this.applicationContext.getBranchCodeFromUrl()}`

   if(shippingProvider){
     url += `&code=${shippingProvider.code}`;
   }
   
   return this.httpClient.get<any>(url, this.apiGatewayRequestOptions).pipe(
      map(res => 
         res.methods.map((m:any) => ({
               id: m.methodGuid,
               title: m.titles && m.titles.length > 0 ? m.titles.find((t: any) => t.languageCode === 'en').title : 'No title',
               legacyId: m.legacyInternalMethodId,
               providerCode: shippingProvider?.code //this won't exist on ititial load where we need to select the provider, not critical
            })).sort((a:any, b:any) => a.title.localeCompare(b.title))
      )
   );
  }

  public getShippingMethodDetails(shippingMethodId: string): Observable<IShippingMethod>{

   return this.httpClient.get<any>(`${this.envService.settings.shippingManagementApi}/methods/${shippingMethodId}`, this.apiGatewayRequestOptions).pipe(
      map((res) => {
         return {
            id: res.method.methodGuid,
            title: res.method.titles && res.method.titles.length > 0 ? res.method.titles.find((t: any) => t.languageCode === 'en').title : 'No title',
            legacyId: res.method.legacyInternalMethodId,
            providerCode: '' //not given by api, not critical
         };
      }),
      catchError((e) => {
         return of({
            title: 'Loading shipping method failed',
            id: shippingMethodId
         } as IShippingMethod)
      })
   );
   
  }
}